/**
 * Embedded-chat runtime shared with app services (api.ts, chatService, authService). In the Report UI it
 * stays inactive and every helper is a no-op, so app behaviour is unchanged.
 */
type HostContext = Record<string, string | number | boolean | null>;

type EmbedRuntime = {
  key: string;
  parentOrigin: string;
  hostContextEnabled: boolean;
  /** Host DOM plain-text summarize after user consent (feature pageContent). */
  pageContentEnabled: boolean;
  /** Called instead of redirecting to /login when the session is gone (401). */
  onUnauthorized: () => void;
};

let runtime: EmbedRuntime | null = null;
/** Latest GARChat('setContext') value; kept even before sign-in so the first message can use it. */
let hostContext: HostContext | null = null;

export function activateEmbedRuntime(next: EmbedRuntime): void {
  runtime = next;
}

export function isEmbedRuntime(): boolean {
  return runtime !== null;
}

export function embedRuntimeKey(): string | null {
  return runtime?.key ?? null;
}

export function isEmbedPageContentEnabled(): boolean {
  return runtime?.pageContentEnabled === true;
}

/** Headers that identify embedded-chat calls to the API (EmbedContextFilter). */
export function embedRequestHeaders(): Record<string, string> {
  if (!runtime) return {};
  return { "X-GAR-Embed-Key": runtime.key, "X-GAR-Embed-Origin": runtime.parentOrigin };
}

export function setEmbedHostContext(context: HostContext | null): void {
  hostContext = context;
}

/** Host context to send with a chat message, when the embed allows it. */
export function embedHostContext(): HostContext | undefined {
  return runtime?.hostContextEnabled && hostContext ? hostContext : undefined;
}

/** @returns true when the embed handled it (no app redirect). */
export function handleEmbedUnauthorized(): boolean {
  if (!runtime) return false;
  runtime.onUnauthorized();
  return true;
}
